import router from "@/router";
import store from "@/state/store";

/**
 * ID de la notificación de WhatsApp Business en el catálogo de notificaciones
 */
export const ID_NOTIFICACION_WHATSAPP = 7;

/**
 * Verifica si el usuario autenticado tiene habilitado el permiso de notificación de WhatsApp
 */
export function tienePermisoNotificacionWhatsapp() {
  if (!store || !store.state || !store.state.todo) return true;
  const usuario = store.state.todo.usuario;
  if (!usuario) return true;

  // Si es usuario root o administrador principal, siempre tiene permiso
  if (usuario.root) return true;
  if (Array.isArray(usuario.roles) && usuario.roles.some((r) => r && (Number(r.id) === 1 || String(r.nombre).toLowerCase().includes("administrador")))) {
    return true;
  }

  // Verificar en la lista de notificaciones activas para sus roles
  if (Array.isArray(usuario.notificaciones)) {
    return usuario.notificaciones.includes(ID_NOTIFICACION_WHATSAPP);
  }

  return true;
}

/**
 * Solicita permisos de notificación al navegador si aún no se han concedido ni denegado.
 */
export async function solicitarPermisoNotificaciones() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  if (Notification.permission === "granted") {
    return "granted";
  }
  if (Notification.permission !== "denied") {
    try {
      const permiso = await Notification.requestPermission();
      return permiso;
    } catch (e) {
      return Notification.permission;
    }
  }
  return Notification.permission;
}

/**
 * Extrae texto legible del mensaje o payload
 */
function extraerTextoMensaje(msg) {
  if (!msg) return "";

  // 1. Campos directos
  if (msg.texto) return msg.texto;
  if (msg.mensaje && typeof msg.mensaje === "string") return msg.mensaje;
  if (msg.text && typeof msg.text === "string") return msg.text;

  // 2. Extraer de payload si es string o json
  let payload = msg.payload;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch (e) {
      payload = null;
    }
  }

  if (payload && typeof payload === "object") {
    if (payload.text && payload.text.body) return payload.text.body;
    if (payload.body) return payload.body;
    if (payload.caption) return payload.caption;

    // Estructura webhook de Meta Cloud API: entry[0].changes[0].value.messages[0]
    const m = payload.entry?.[0]?.changes?.[0]?.value?.messages?.[0] || payload.messages?.[0];
    if (m) {
      if (m.text?.body) return m.text.body;
      if (m.image?.caption) return `📷 ${m.image.caption}`;
      if (m.type === "image") return "📷 Imagen";
      if (m.type === "audio" || m.type === "voice") return "🎵 Mensaje de voz";
      if (m.type === "document") return `📄 ${m.document?.filename || "Documento"}`;
      if (m.type === "video") return "🎥 Video";
      if (m.type === "sticker") return "🏷️ Sticker";
      if (m.type === "location") return "📍 Ubicación";
    }
  }

  // 3. Tipos multimedia directos
  const tipo = msg.tipo || msg.type;
  if (tipo === "image") return "📷 Imagen";
  if (tipo === "audio" || tipo === "voice") return "🎵 Mensaje de voz";
  if (tipo === "document") return `📄 ${msg.filename || "Documento"}`;
  if (tipo === "video") return "🎥 Video";
  if (tipo === "sticker") return "🏷️ Sticker";
  if (tipo === "location") return "📍 Ubicación";

  return "Nuevo mensaje de WhatsApp";
}

/**
 * Muestra una notificación nativa del sistema estilo Chrome/Gmail
 * a partir de la data recibida en el evento WebSocket\ConversacionesWhatsapp.
 *
 * @param {Object} data Objeto emitido por el evento WebSocket\ConversacionesWhatsapp
 */
export function notificarMensajeWhatsapp(data) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (!tienePermisoNotificacionWhatsapp()) return;
  if (!data) return;

  const conversacion = data.conversacion || data.data || data;

  // Si proviene de una acción manual como "marcar como no leído", no lanzar notificación push
  if (conversacion.accion_marcar_no_leido || data.accion_marcar_no_leido) {
    return;
  }

  const ultimoMensaje =
    conversacion.ultimo_mensaje_whatsapp ||
    (Array.isArray(conversacion.mensajes) && conversacion.mensajes.length > 0
      ? conversacion.mensajes[conversacion.mensajes.length - 1]
      : null) ||
    data.mensaje ||
    null;

  // Solo notificar si es un mensaje ENTRANTE (evitar notificar mensajes salientes que nosotros enviamos)
  if (ultimoMensaje && ultimoMensaje.direccion && ultimoMensaje.direccion !== "entrante") {
    return;
  }

  // Si el mensaje tiene más de 30 segundos de haber sido creado, no es un mensaje entrante nuevo, sino una recarga o marcado manual
  if (ultimoMensaje && ultimoMensaje.created_at) {
    const fechaMsg = new Date(ultimoMensaje.created_at).getTime();
    if (!isNaN(fechaMsg) && Date.now() - fechaMsg > 45000) {
      return;
    }
  }

  // Obtener nombre del remitente / cliente
  const cliente = conversacion.cliente || {};
  let nombreContacto =
    cliente.nombre_completo ||
    cliente.nombre ||
    conversacion.nombre ||
    conversacion.nombre_contacto ||
    "";

  if (!nombreContacto && (conversacion.numero || data.telefono)) {
    nombreContacto = `+${conversacion.numero || data.telefono}`;
  }
  if (!nombreContacto) {
    nombreContacto = "WhatsApp";
  }

  // Cuerpo del mensaje
  const cuerpo = extraerTextoMensaje(ultimoMensaje) || "Nuevo mensaje recibido";

  const idTelefono = conversacion.id || conversacion.id_telefono_cliente || null;
  const numero = conversacion.numero || data.telefono || "";
  const idCliente = conversacion.id_cliente || cliente.id || null;

  try {
    const notif = new Notification(nombreContacto, {
      body: cuerpo,
      icon: "/img/icons/argusblack-192x192.png",
      badge: "/img/icons/argusblack-192x192.png",
      tag: `whatsapp_${idTelefono || numero || Date.now()}`,
      renotify: true,
      silent: false
    });

    notif.onclick = () => {
      window.focus();
      try {
        notif.close();
      } catch (e) { }

      // Redirigir a la vista de mensajes de WhatsApp abriendo el chat
      if (router) {
        const query = {};
        if (idTelefono) query.id_telefono = idTelefono;
        else if (numero) query.numero = numero;
        if (idCliente) query.id_cliente = idCliente;

        router.push({
          name: "whatsappMensajes",
          query
        }).catch(() => { });
      }
    };
  } catch (err) {
    // Si falla la API nativa de Notification (por ejemplo en móviles o contextos seguros)
    console.warn("No se pudo mostrar la notificación de escritorio:", err);
  }
}

